import { CardSaveBadge, type CardSavePhase } from './CardSaveBadge';

const BEDROOM_OPTIONS = ['1', '2', '3', '4', '5', '6', '7+'] as const;

const BEDROOM_CHOICE_LABELS = ['First choice', 'Second choice', 'Third choice'] as const;
const MAX_BEDROOM_CHOICES = BEDROOM_CHOICE_LABELS.length;

/** Visible May 1 through October 15, Eastern time. Hidden the rest of the year. */
function seasonalAvailabilityBannerVisible(now = new Date()): boolean {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(now);
  const month = Number(parts.find((part) => part.type === 'month')?.value);
  const day = Number(parts.find((part) => part.type === 'day')?.value);
  const monthDay = month * 100 + day;
  return monthDay >= 501 && monthDay <= 1015;
}

/** UISwitch-style track (51×31 pt). */
function IosSwitchVisual({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`relative inline-block h-[31px] w-[51px] shrink-0 rounded-full transition-[background-color] duration-200 ease-out ${
        checked ? 'bg-[#34C759]' : 'bg-[#E9E9EA] dark:bg-gray-600'
      }`}
    >
      <span
        className={`absolute top-[2px] h-[27px] w-[27px] rounded-full bg-white transition-transform duration-200 ease-out ${
          checked ? 'translate-x-[22px]' : 'translate-x-[2px]'
        }`}
        style={{
          boxShadow: '0 3px 8px rgba(0, 0, 0, 0.15), 0 3px 1px rgba(0, 0, 0, 0.06)',
        }}
      />
    </span>
  );
}

function formatBedroomOptionLabel(value: string): string {
  return `${value} bed${value === '1' ? '' : 's'}`;
}

function choiceLabelForRankIndex(index: number): string | null {
  return BEDROOM_CHOICE_LABELS[index] ?? null;
}

interface PropertyNotificationBedroomPickerProps {
  rankings: string[];
  generalPool: boolean;
  onChange: (next: { rankings: string[]; generalPool: boolean }) => void;
  className?: string;
  /** Condensed bordered grid for My Information bento (one column wide). */
  layout?: 'default' | 'bento';
  complete?: boolean;
  savePhase?: CardSavePhase;
}

interface NotificationToggleRowProps {
  id: string;
  title: string;
  choiceLabel?: string | null;
  checked: boolean;
  disabled?: boolean;
  dense?: boolean;
  /** Same text-sm for title and rank label (bento grid). */
  uniformText?: boolean;
  onToggle: () => void;
}

function NotificationToggleRow({
  id,
  title,
  choiceLabel,
  checked,
  disabled = false,
  dense = false,
  uniformText = false,
  onToggle,
}: NotificationToggleRowProps) {
  /** Match My Information field values (`text-base` on pickers/inputs), not label `text-sm`. */
  const textClass = 'text-base font-medium leading-snug';

  return (
    <label
      htmlFor={id}
      className={`flex w-full cursor-pointer items-center gap-2 ${dense ? 'py-1.5' : 'gap-3 py-3'} ${
        disabled ? 'cursor-not-allowed opacity-45' : ''
      }`}
    >
      <span className="min-w-0 flex-1">
        {uniformText && choiceLabel ? (
          <span className={`block ${textClass} text-gray-900 dark:text-white`}>
            {title}
            <span className="font-medium text-gray-500 dark:text-gray-400"> · {choiceLabel}</span>
          </span>
        ) : (
          <>
            <span className={`block ${textClass} text-gray-900 dark:text-white`}>{title}</span>
            {choiceLabel ? (
              <span className={`mt-0.5 block ${textClass} text-gray-500 dark:text-gray-400`}>
                {choiceLabel}
              </span>
            ) : null}
          </>
        )}
      </span>
      <input
        id={id}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={() => {
          if (!disabled) onToggle();
        }}
        className="peer sr-only"
      />
      <IosSwitchVisual checked={checked} />
    </label>
  );
}

type NotificationColumnItem = { kind: 'general' } | { kind: 'bedroom'; option: string };

function notificationItems(): NotificationColumnItem[] {
  return [{ kind: 'general' }, ...BEDROOM_OPTIONS.map((option) => ({ kind: 'bedroom' as const, option }))];
}

export default function PropertyNotificationBedroomPicker({
  rankings,
  generalPool,
  onChange,
  className = '',
  layout = 'default',
  complete = false,
  savePhase = 'idle',
}: PropertyNotificationBedroomPickerProps) {
  const atBedroomLimit = rankings.length >= MAX_BEDROOM_CHOICES;

  const toggleGeneralPool = () => {
    onChange({ rankings, generalPool: !generalPool });
  };

  const toggleBedroom = (option: string) => {
    const existingIndex = rankings.indexOf(option);
    if (existingIndex !== -1) {
      onChange({
        rankings: rankings.filter((value) => value !== option),
        generalPool,
      });
      return;
    }
    if (rankings.length >= MAX_BEDROOM_CHOICES) return;
    onChange({ rankings: [...rankings, option], generalPool });
  };

  const sectionLayoutClass =
    layout === 'bento' ? 'flex h-full min-h-0 flex-col' : 'flex h-full flex-col';

  return (
    <section
      className={`relative ${sectionLayoutClass} rounded-2xl border border-gray-200 bg-white px-4 py-4 shadow-bento dark:border-white/10 dark:bg-gray-800 sm:px-6 sm:py-5 ${className}`.trim()}
    >
      <CardSaveBadge phase={savePhase} complete={complete} />
      <h2 className="pr-8 text-lg font-bold text-gray-900 dark:text-white">Notifications</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {layout === 'bento'
          ? 'Up to three bedroom sizes — order turned on is 1st, 2nd, 3rd choice.'
          : 'Turn on notifications to get updates on properties with the following characteristics.'}
      </p>
      {layout === 'default' ? (
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Choose up to three bedroom sizes. The order you turn them on is your preference — first on is your{' '}
          <span className="font-medium">first choice</span>, then <span className="font-medium">second</span>, then{' '}
          <span className="font-medium">third</span>.
        </p>
      ) : null}
      {seasonalAvailabilityBannerVisible() ? (
        <div
          className={`rounded-xl border border-amber-200/90 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-950 ${
            layout === 'bento' ? 'mt-2 px-2 py-2' : 'mt-4 px-3 py-2.5 sm:px-3.5 sm:py-3'
          }`}
          role="note"
        >
          <p
            className="text-center text-sm font-medium leading-snug text-amber-950 dark:text-amber-100"
          >
            <span aria-hidden="true">⚠️ </span>
            {layout === 'bento'
              ? 'First units expected mid-October.'
              : 'We expect our first units to become available in mid-October.'}
            <span aria-hidden="true"> ⚠️</span>
          </p>
        </div>
      ) : null}

      {layout === 'bento' ? (
        <div className="mt-3 flex min-h-0 flex-1 flex-col">
          <div
            className="grid min-h-0 flex-1 grid-cols-2 overflow-hidden rounded-lg border border-gray-200 bg-gray-50/50 dark:border-white/15 dark:bg-white/[0.02]"
            role="group"
            aria-label="Notification preferences"
          >
            {notificationItems().map((item, index) => {
              const cellBase =
                'flex min-h-[3rem] items-center border-b border-gray-200 bg-white px-3 dark:border-white/10 dark:bg-gray-800';
              if (item.kind === 'general') {
                return (
                  <div key="general" className={`col-span-2 ${cellBase}`}>
                    <NotificationToggleRow
                      id="notification-general"
                      title="General"
                      checked={generalPool}
                      dense
                      uniformText
                      onToggle={toggleGeneralPool}
                    />
                  </div>
                );
              }
              const bedroomIndex = index - 1;
              const isLeftColumn = bedroomIndex % 2 === 0;
              const lastBedroomRowIndex = Math.floor((BEDROOM_OPTIONS.length - 1) / 2);
              const isBottomRow = Math.floor(bedroomIndex / 2) === lastBedroomRowIndex;
              return (
                <div
                  key={item.option}
                  className={`${cellBase} ${isLeftColumn ? 'border-r border-gray-200 dark:border-white/10' : ''} ${
                    isBottomRow ? 'border-b-0' : ''
                  }`}
                >
                  <NotificationToggleRow
                    id={`notification-bedroom-${item.option}`}
                    title={formatBedroomOptionLabel(item.option)}
                    choiceLabel={
                      rankings.includes(item.option)
                        ? choiceLabelForRankIndex(rankings.indexOf(item.option))
                        : null
                    }
                    checked={rankings.includes(item.option)}
                    disabled={!rankings.includes(item.option) && atBedroomLimit}
                    dense
                    uniformText
                    onToggle={() => toggleBedroom(item.option)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="mt-2 grid grid-cols-1 sm:grid-flow-col sm:grid-cols-3 sm:grid-rows-3 sm:gap-x-10">
          {notificationItems().map((item) =>
            item.kind === 'general' ? (
              <NotificationToggleRow
                key="general"
                id="notification-general"
                title="General notifications"
                checked={generalPool}
                onToggle={toggleGeneralPool}
              />
            ) : (
              <NotificationToggleRow
                key={item.option}
                id={`notification-bedroom-${item.option}`}
                title={formatBedroomOptionLabel(item.option)}
                choiceLabel={
                  rankings.includes(item.option)
                    ? choiceLabelForRankIndex(rankings.indexOf(item.option))
                    : null
                }
                checked={rankings.includes(item.option)}
                disabled={!rankings.includes(item.option) && atBedroomLimit}
                onToggle={() => toggleBedroom(item.option)}
              />
            ),
          )}
        </div>
      )}
    </section>
  );
}
